import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ApiTypesEnum, DHLAvailableProductDTO, DHLAvailableProductsRequestDTO, DHLPackageDTO } from '@api/ringil-api.v2';
import { Carrier } from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { ApiContext } from '@api/api.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { Calendar } from 'primereact/calendar';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import {
  dayjsFromApiUtc,
  dateToTimeString,
  formatDateFromStringWithLocale,
  formatDateTimeFromDayjsInTzWithLocale,
  timeStringToDate,
} from '@utils/dateWithTz.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import { InputText } from 'primereact/inputtext';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { useDebounce } from 'use-debounce';

interface ComponentProps {
  values: Carrier[];
  onSelect: (values: Carrier[]) => void;
  productParameters: DHLAvailableProductsRequestDTO;
  pickupTimezoneId?: string;
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

const minutesFromHHmm = (value?: string): number | null => {
  if (!value || !HHMM.test(value)) return null;
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
};

const compareHHmm = (a: string, b: string): number => {
  return (minutesFromHHmm(a) ?? 0) - (minutesFromHHmm(b) ?? 0);
};

const trimToHHmm = (value?: string): string | undefined => {
  if (!value) return undefined;
  return value.length >= 5 ? value.substring(0, 5) : value;
};

// A package is valid only when all of its required numeric fields are provided and positive.
// Missing values (e.g. an empty amount) make the DHL endpoint return an error, so we skip the call.
const isValidNumber = (value?: number): boolean => typeof value === 'number' && !isNaN(value) && value > 0;

const isValidPackage = (pkg?: DHLPackageDTO): boolean =>
  !!pkg && isValidNumber(pkg.amount) && isValidNumber(pkg.weight) && isValidNumber(pkg.length) && isValidNumber(pkg.width) && isValidNumber(pkg.height);

// Only the non-optional fields of DHLAvailableProductsRequestDTO are mandatory for the request.
// Without all of them DHL cannot resolve products, so we skip the call entirely.
const hasMandatoryParameters = (p?: DHLAvailableProductsRequestDTO): boolean =>
  !!p &&
  !!p.countryFrom &&
  !!p.countryTo &&
  !!p.postalCodeFrom &&
  !!p.postalCodeTo &&
  !!p.cityFrom &&
  !!p.cityTo &&
  Array.isArray(p.allPackages) &&
  p.allPackages.length > 0 &&
  p.allPackages.every(isValidPackage);

export const DHLProductTerms: React.FC<ComponentProps> = ({
                                                            values,
                                                            onSelect,
                                                            productParameters,
                                                            pickupTimezoneId,
                                                          }) => {


  const { tr } = useTranslationLgs();
  const [productTypes, setProductTypes] = useState<DHLAvailableProductDTO[]>([]);
  const { getDHLAvailableProducts } = useContext(ApiContext);
  const { showToastMessage } = useContext(ToastContext);
  const [isDhl, setIsDhl] = useState<boolean>(false);
  const [dhlCarriers, setDhlCarriers] = useState<Carrier[]>([]);
  const [validationError, setValidationError] = useState<{ message: string; params?: string[] } | null>(null);

  // The parent rebuilds productParameters as a fresh object literal on every render, so depending on its
  // reference would re-request DHL on every render (even when unrelated data such as cargo type changes).
  // Memoize by serialized value so the request only fires when a relevant field actually changes.
  const productParametersKey = JSON.stringify(productParameters ?? {});
  const stableProductParameters = useMemo(() => productParameters, [productParametersKey]);
  // Debounce so rapid edits (typing an address, adjusting packages) collapse into a single request
  // instead of firing one call per keystroke.
  const [debouncedProductParameters] = useDebounce(stableProductParameters, 500);

  useEffect(() => {
    const hasDhlApi = Array.isArray(values) && values.some(carrier =>
      Array.isArray(carrier.apiTypes) && carrier.apiTypes.includes(ApiTypesEnum.DHL),
    );

    setIsDhl(hasDhlApi);

    const dhlCarriers = Array.isArray(values)
      ? values.filter(carrier =>
        Array.isArray(carrier.apiTypes) && carrier.apiTypes.includes(ApiTypesEnum.DHL),
      )
      : [];
    setDhlCarriers(dhlCarriers);

  }, [values]);

  useEffect(() => {
    if (isDhl && hasMandatoryParameters(debouncedProductParameters)) {
      getDHLAvailableProducts(
        debouncedProductParameters,
        values => {
          setValidationError(null);
          setProductTypes(values);
          const availableCodes = values.map(p => p.productCode as string);

          setDhlCarriers(prev => {
            const updated = prev.map(item => {
              const stillAvailable = availableCodes.includes(item._dhlProductTypeCode);
              const productCode = stillAvailable ? item._dhlProductTypeCode : values[0]?.productCode;
              const product = values.find(p => p.productCode === productCode);
              const isFirstSeed = item._dhlCourierRequested === undefined;
              const defaultEarliest = trimToHHmm(product?.pickupCapabilities?.pickupEarliest);
              const defaultLatest = trimToHHmm(product?.pickupCapabilities?.pickupLatest);
              return {
                ...item,
                _dhlProductTypeCode: productCode,
                _dhlPickupCapabilities: product?.pickupCapabilities,
                _dhlCourierRequested: isFirstSeed ? true : item._dhlCourierRequested,
                _dhlPickupReadyTime: isFirstSeed
                  ? item._dhlPickupReadyTime ?? defaultEarliest
                  : item._dhlPickupReadyTime,
                _dhlPickupCloseTime: isFirstSeed
                  ? item._dhlPickupCloseTime ?? defaultLatest
                  : item._dhlPickupCloseTime,
              } as Carrier;
            });

            if (JSON.stringify(updated) !== JSON.stringify(prev)) {
              onSelect(updated);
            }

            return updated;
          });
        },
        null,
        {
          onValidationFailed: (response) => {
            if (response?.length > 0) {
              const dhlMessage = response[0];
              if (dhlMessage) {
                setValidationError({
                  message: dhlMessage.message,
                  params: dhlMessage.params,
                });
              } else {
                setValidationError({
                  message: 'DHL validation error occurred',
                  params: [],
                });
              }
            }
          },
          onError: (error) => {
            showToastMessage(
              tr('api.errorApi', 'Error'),
              <>
                <div className="toast-wrapper">
                  <div>{error.message}</div>
                  <div>
                    <strong>getDHLAvailableProducts</strong> - {error?.config?.method} - {error?.config?.url}
                  </div>
                </div>
              </>,
              'error',
              5000,
            );
          },
        }
      );
    }
  }, [isDhl, debouncedProductParameters]);

  const updateDhlCarriers = (mutator: (carrier: Carrier) => Carrier) => {
    const updated = dhlCarriers.map(mutator);
    onSelect(updated);
  };

  const handleApiProductSelect = (_dhlProductTypeCode: string) => {
    const product = productTypes?.find(p => p.productCode === _dhlProductTypeCode);
    updateDhlCarriers(item => ({
      ...item,
      _dhlProductTypeCode,
      _dhlPickupCapabilities: product?.pickupCapabilities,
    }));
  };

  const selectedProductCode = dhlCarriers[0]?._dhlProductTypeCode || (productTypes && productTypes[0]?.productCode);
  const selectedProduct = productTypes?.find(pt => pt.productCode === selectedProductCode);
  const pickupCapabilities = selectedProduct?.pickupCapabilities;

  const courierRequested = !!dhlCarriers[0]?._dhlCourierRequested;
  const readyTime = trimToHHmm(dhlCarriers[0]?._dhlPickupReadyTime);
  const closeTime = trimToHHmm(dhlCarriers[0]?._dhlPickupCloseTime);

  const pickupDateRaw = pickupCapabilities?.localCutoffDateAndTime?.substring(0, 10);
  const pickupDateLabel = useMemo(
    () => (pickupDateRaw ? formatDateFromStringWithLocale(pickupDateRaw) : null),
    [pickupDateRaw],
  );

  const earliest = trimToHHmm(pickupCapabilities?.pickupEarliest);
  const latest = trimToHHmm(pickupCapabilities?.pickupLatest);

  const handleToggleCourier = (next: boolean) => {
    updateDhlCarriers(item => ({
      ...item,
      _dhlCourierRequested: next,
      ...(next
        ? {
            _dhlPickupReadyTime: item._dhlPickupReadyTime ?? earliest,
            _dhlPickupCloseTime: item._dhlPickupCloseTime ?? latest,
          }
        : { _dhlPickupReadyTime: undefined, _dhlPickupCloseTime: undefined }),
    }));
  };

  const handleReadyTimeChange = (value: string | null) => {
    updateDhlCarriers(item => ({ ...item, _dhlPickupReadyTime: value ?? undefined }));
  };

  const handleCloseTimeChange = (value: string | null) => {
    updateDhlCarriers(item => ({ ...item, _dhlPickupCloseTime: value ?? undefined }));
  };

  const readyError = useMemo(() => {
    if (!courierRequested) return null;
    if (!readyTime) return tr('DHLProductTerms.errorRequired', 'Pickup times are required when ordering a courier');
    if (earliest && compareHHmm(readyTime, earliest) < 0) {
      return tr('DHLProductTerms.errorWindow', 'Time must be within the DHL pickup window {{earliest}}–{{latest}}', {
        earliest,
        latest,
      });
    }
    if (latest && compareHHmm(readyTime, latest) > 0) {
      return tr('DHLProductTerms.errorWindow', 'Time must be within the DHL pickup window {{earliest}}–{{latest}}', {
        earliest,
        latest,
      });
    }
    return null;
  }, [courierRequested, readyTime, earliest, latest, tr]);

  const closeError = useMemo(() => {
    if (!courierRequested) return null;
    if (!closeTime) return tr('DHLProductTerms.errorRequired', 'Pickup times are required when ordering a courier');
    if (earliest && compareHHmm(closeTime, earliest) < 0) {
      return tr('DHLProductTerms.errorWindow', 'Time must be within the DHL pickup window {{earliest}}–{{latest}}', {
        earliest,
        latest,
      });
    }
    if (latest && compareHHmm(closeTime, latest) > 0) {
      return tr('DHLProductTerms.errorWindow', 'Time must be within the DHL pickup window {{earliest}}–{{latest}}', {
        earliest,
        latest,
      });
    }
    if (readyTime && HHMM.test(readyTime) && HHMM.test(closeTime)) {
      const gap = minutesFromHHmm(closeTime)! - minutesFromHHmm(readyTime)!;
      if (gap < 120) {
        return tr('DHLProductTerms.errorMinGap', 'Close time must be at least 2 hours after ready time');
      }
    }
    return null;
  }, [courierRequested, readyTime, closeTime, earliest, latest, tr]);

  const tzWarning = courierRequested && !pickupTimezoneId
    ? tr('DHLProductTerms.errorMissingTz', 'Pickup location timezone is missing — fill in the loading location first')
    : null;

  const differentPayer = !!dhlCarriers[0]?._dhlDifferentPayer;
  const payerAccountNumber = dhlCarriers[0]?._dhlPayerAccountNumber ?? '';
  const applyToDutiesTaxes = !!dhlCarriers[0]?._dhlApplyToDutiesTaxes;

  const payerError = useMemo(() => {
    if (!differentPayer) return null;
    if (!payerAccountNumber.trim()) {
      return tr('DHLProductTerms.payerAccountRequired', 'Payer account number is required');
    }
    return null;
  }, [differentPayer, payerAccountNumber, tr]);

  const handleToggleDifferentPayer = (next: boolean) => {
    updateDhlCarriers(item => ({
      ...item,
      _dhlDifferentPayer: next,
      ...(next
        ? {}
        : { _dhlPayerAccountNumber: undefined, _dhlApplyToDutiesTaxes: false }),
    }));
  };

  const handlePayerAccountNumberChange = (value: string) => {
    updateDhlCarriers(item => ({ ...item, _dhlPayerAccountNumber: value }));
  };

  const handleToggleApplyToDutiesTaxes = (next: boolean) => {
    updateDhlCarriers(item => ({ ...item, _dhlApplyToDutiesTaxes: next }));
  };

  return (
    <>
      {isDhl && (
        <div className="p-4 rounded-lg bg-base-tertiary gap-4 flex flex-col">
          {validationError ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="text-sm font-medium text-red-800">
                {tr('DHLProductTerms.validationError', 'DHL Validation Error')}
              </div>
              <div className="text-sm text-red-700 mt-2">
                <div className="font-medium">{validationError.message}</div>
                {validationError.params && validationError.params.length > 0 && (
                  <div className="mt-2 text-xs">
                    {validationError.params.map((param, index) => (
                      <div key={index} className="mt-1">{param}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <Field
                label={tr('DHLProductTerms.dhlProduct', 'DHL - select product')}
                required
              >
                <Dropdown
                  options={productTypes?.map(pt => ({
                    label: pt.productName,
                    value: pt.productCode,
                  }))}
                  optionLabel="label"
                  optionValue="value"
                  value={selectedProductCode}
                  onChange={e => handleApiProductSelect(e.value)}
                  style={{ width: '100%' }}
                  placeholder={tr('SchenkerProductTerms.schenkerProductPlaceholder', 'Select product')}
                />
              </Field>

              {selectedProduct?.price && (
                <div>
                  <div className="text-sm font-medium text-content-primary">
                    {tr('DHLProductTerms.price', 'Price')}
                  </div>
                  <span className="text-content-secondary">
                    {selectedProduct.price.price} {selectedProduct.price.currency}
                  </span>
                </div>
              )}

              {selectedProduct?.estimatedDeliveryDate && (
                <div>
                  <div className="text-sm font-medium text-content-primary">
                    {tr('DHLProductTerms.estimatedDeliveryDate', 'Estimated Delivery Date')}
                  </div>
                  <span className="text-content-secondary">
                    {formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiUtc(selectedProduct.estimatedDeliveryDate), "UTC")}
                  </span>
                </div>
              )}

              {pickupCapabilities ? (
                <div className="flex flex-col gap-3 mt-2">
                  <InputCheckbox
                    label={tr('DHLProductTerms.courierRequested', 'Order DHL courier pickup')}
                    value={courierRequested}
                    onChange={(_, value) => handleToggleCourier(!!value)}
                  />

                  {courierRequested && (
                    <div className="flex flex-col gap-3">
                      <div>
                        <div className="text-sm font-medium text-content-primary">
                          {tr('DHLProductTerms.pickupDate', 'Pickup date')}
                        </div>
                        <span className="text-content-secondary">
                          {pickupDateLabel ?? '—'}
                        </span>
                      </div>

                      <Field
                        label={tr('DHLProductTerms.pickupReadyTime', 'Pickup ready time')}
                        required
                        errorMessage={readyError}
                      >
                        <Calendar
                          timeOnly
                          hourFormat="24"
                          value={timeStringToDate(readyTime)}
                          onChange={e => handleReadyTimeChange(dateToTimeString(e.value as Date))}
                          style={{ width: '100%' }}
                        />
                      </Field>

                      <Field
                        label={tr('DHLProductTerms.pickupCloseTime', 'Pickup close time')}
                        required
                        errorMessage={closeError}
                      >
                        <Calendar
                          timeOnly
                          hourFormat="24"
                          value={timeStringToDate(closeTime)}
                          onChange={e => handleCloseTimeChange(dateToTimeString(e.value as Date))}
                          style={{ width: '100%' }}
                        />
                      </Field>

                      {earliest && latest && (
                        <div className="text-xs text-content-secondary">
                          {tr('DHLProductTerms.windowHint', 'Allowed window: {{earliest}} – {{latest}}', {
                            earliest,
                            latest,
                          })}
                        </div>
                      )}

                      {tzWarning && (
                        <div className="text-sm text-red-700">{tzWarning}</div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-content-secondary mt-2">
                  {tr('DHLProductTerms.noCourierPickup', 'DHL courier pickup is not available for this product.')}
                </div>
              )}

              <div className="flex flex-col gap-3 mt-2 border-t border-base-secondary pt-3">
                <InputCheckbox
                  label={tr('DHLProductTerms.differentPayer', 'Different payer')}
                  value={differentPayer}
                  onChange={(_, value) => handleToggleDifferentPayer(!!value)}
                />

                {differentPayer && (
                  <div className="flex flex-col gap-3">
                    <Field
                      label={tr('DHLProductTerms.payerAccountNumber', 'DHL payer account number')}
                      required
                      errorMessage={payerError}
                    >
                      <InputText
                        value={payerAccountNumber}
                        onChange={e => handlePayerAccountNumberChange(e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </Field>

                    <InputCheckbox
                      label={tr('DHLProductTerms.applyToDutiesAndTaxes', 'Pay duties & taxes from this account too')}
                      value={applyToDutiesTaxes}
                      onChange={(_, value) => handleToggleApplyToDutiesTaxes(!!value)}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};
